// AuthContext.js - Authentication context provider for managing user auth state and tokens
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

// Create authentication context for sharing auth state across components
const AuthContext = createContext();

// Custom hook to access authentication context
export const useAuth = () => useContext(AuthContext);

/**
 * AuthProvider - Context provider component that manages authentication state
 * Handles user login/logout, token management, and auth status checking
 * @param {ReactNode} children - Child components that need access to auth context
 */
export const AuthProvider = ({ children }) => {
    // Current authenticated user data
    const [user, setUser] = useState(null);
    // Authentication error messages
    const [error, setError] = useState('');
    // Boolean flag for authentication status
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    // Callback function to notify components of login status changes
    const [loginStatusCallback, setLoginStatusCallback] = useState(null);
    // Cached access token for API requests
    const [accessToken, setAccessToken] = useState(null);
    // Timestamp when the current token expires
    const [tokenExpiresAt, setTokenExpiresAt] = useState(0);
    // Flag to prevent concurrent token fetch requests
    const [isFetchingToken, setIsFetchingToken] = useState(false);

    /**
     * Register a callback function to be notified of login status changes
     * @param {function} callback - Function to call when auth status changes
     */
    const registerLoginStatusCallback = (callback) => {
        setLoginStatusCallback(() => callback);
    };

    /**
     * Check current authentication status by calling the profile endpoint
     * Updates user state and authentication status based on server response
     */
    const checkAuthStatus = async () => {
        try {
            console.log('[Auth Context] Checking authentication status...');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
                credentials: 'include', // Include session cookies
            });

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                setIsAuthenticated(true);
                loginStatusCallback?.(true);
                console.log('[Auth Context] User is authenticated:', userData.name);
            } else {
                setUser(null);
                setIsAuthenticated(false);
                loginStatusCallback?.(false);
                console.log('[Auth Context] User is not authenticated');
            }
        } catch (err) {
            console.error('[Auth Context] Error checking auth status:', err);
            setUser(null);
            setIsAuthenticated(false);
            loginStatusCallback?.(false);
        }
    };

    /**
     * Get a valid access token for API requests
     * Returns cached token if valid, otherwise fetches a new one from the server
     * Implements token caching and prevents concurrent requests
     * @returns {string|null} Access token or null if authentication failed
     */
    const getAccessToken = async () => {
        // Check if we have a valid cached token (with 2 minute buffer for safety)
        if (accessToken && Date.now() < tokenExpiresAt - 120000) {
            console.log('[Auth Context] Using cached access token');
            return accessToken;
        }

        // Prevent multiple concurrent token requests to avoid race conditions
        if (isFetchingToken) {
            console.log('[Auth Context] Token fetch already in progress, waiting...');
            // Wait for the current fetch to complete (up to 5 seconds)
            let attempts = 0;
            while (isFetchingToken && attempts < 50) {
                await new Promise(resolve => setTimeout(resolve, 100));
                attempts++;
            }
            // Return the token if it was successfully fetched during wait
            if (accessToken && Date.now() < tokenExpiresAt - 60000) {
                return accessToken;
            }
        }

        setIsFetchingToken(true);
        try {
            console.log('[Auth Context] Fetching new access token...');
            const tokenResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/token`, {
                credentials: 'include',
            });

            if (!tokenResponse.ok) {
                const errorData = await tokenResponse.json().catch(() => ({ error: 'Unknown error' }));
                console.error('[Auth Context] Token request failed:', errorData);

                // If token refresh failed and requires login, redirect to login
                if (errorData.requiresLogin || tokenResponse.status === 401) {
                    console.log('[Auth Context] Session expired, redirecting to login...');
                    setUser(null);
                    setIsAuthenticated(false);
                    loginStatusCallback?.(false);
                    // Don't throw error, let the app handle the unauthenticated state
                    return null;
                }

                throw new Error(errorData.error || errorData.message || 'Token refresh failed');
            }

            const { access_token, expires_in } = await tokenResponse.json();
            setAccessToken(access_token);
            setTokenExpiresAt(Date.now() + expires_in * 1000);
            console.log('[Auth Context] New access token obtained, expires in:', expires_in, 'seconds');
            return access_token;
        } catch (err) {
            console.error('[Auth Context] Error obtaining access token:', err.message);

            // If it's a network error or other non-auth error, still throw
            if (!err.message.includes('Token refresh failed') && !err.message.includes('Session expired')) {
                throw err;
            }

            // For auth-related errors, set unauthenticated state
            setUser(null);
            setIsAuthenticated(false);
            loginStatusCallback?.(false);
            return null;
        } finally {
            setIsFetchingToken(false);
        }
    };

    useEffect(() => {
        console.log('[Auth Context] Component mounted, checking auth status...');

        // Check for URL parameters (auth success/error)
        const urlParams = new URLSearchParams(window.location.search);
        const authResult = urlParams.get('auth');
        const authError = urlParams.get('error');

        if (authResult === 'success') {
            console.log('[Auth Context] Authentication successful from URL params');
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            // Check auth status
            checkAuthStatus();
        } else if (authError) {
            console.error('[Auth Context] Authentication error from URL params:', authError);
            setError(decodeURIComponent(authError));
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
        } else {
            // Normal page load, check auth status
            checkAuthStatus();
        }
    }, [loginStatusCallback]);

    useEffect(() => {
        if (isAuthenticated) {
            getAccessToken().catch(() => { });
        }
    }, [isAuthenticated]);

    const handleLogout = async () => {
        try {
            console.log('[Auth Context] Logging out...');

            // First, logout from Autodesk
            const iframe = document.createElement('iframe');
            iframe.style.visibility = 'hidden';
            iframe.src = 'https://accounts.autodesk.com/Authentication/LogOut';
            document.body.appendChild(iframe);

            iframe.onload = async () => {
                document.body.removeChild(iframe);
                // Then logout from our server
                window.location.replace(`${process.env.REACT_APP_API_URL}/api/auth/logout`);
            };
        } catch (error) {
            console.error('[Auth Context] Logout failed:', error.message);
            setError('Failed to logout. Please try again.');
        }
    };

    const handleLogin = () => {
        console.log('[Auth Context] Initiating login...');
        const loginUrl = `${process.env.REACT_APP_API_URL}/api/auth/login`;
        console.log('[Auth Context] Redirecting to:', loginUrl);
        window.location.href = loginUrl;
    };

    useInterval(checkAuthStatus, 15 * 60 * 1000); // Check every 15 minutes

    return (
        <AuthContext.Provider value={{
            user,
            error,
            isAuthenticated,
            isFetchingToken,
            handleLogout,
            handleLogin,
            getAccessToken,
            checkAuthStatus,
            registerLoginStatusCallback
        }}>
            {children}
        </AuthContext.Provider>
    );
};

function useInterval(callback, delay) {
    const savedCallback = useRef();

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}
